import { useEffect, useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Send, Copy, Clock, CheckCircle2, MailPlus } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LEVEL2 = new Set(["introducer", "custodian", "guarantor"]);

/**
 * SponsorInvites — Level-2 sponsor (Introducer / Custodian / Guarantor) sends
 * an invite email to onboard a dependent Super-Stockist.  Shows sent invites
 * with expiry/used status and one-click "Copy link".
 */
export const SponsorInvites = () => {
  const { user } = useAuth();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [biz, setBiz] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => axios.get(`${API}/sponsor/invites`)
    .then((r) => setInvites(r.data || []))
    .catch(() => setInvites([]))
    .finally(() => setLoading(false));

  useEffect(() => {
    if (!user || !LEVEL2.has(user.buyer_category)) { setLoading(false); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!user || !LEVEL2.has(user.buyer_category)) return null;
  if (loading) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error("Email is required"); return; }
    setSending(true);
    try {
      await axios.post(`${API}/sponsor/invites`, {
        email: email.trim().toLowerCase(),
        business_name: biz.trim() || undefined,
        note: note.trim() || undefined,
      });
      toast.success("Invite sent — expires in 7 days");
      setEmail(""); setBiz(""); setNote("");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invite");
    } finally {
      setSending(false);
    }
  };

  const copy = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("Invite link copied"); }
    catch { toast.error("Copy failed — long-press the link"); }
  };

  return (
    <div data-testid="sponsor-invites" className="mt-16">
      <Toaster position="bottom-right" theme="dark" richColors />
      <h2 className="font-serif text-2xl md:text-3xl mb-4 flex items-center gap-3">
        <MailPlus size={20} className="text-[#B87333]" />
        Invite a dependent Super-Stockist
      </h2>
      <p className="font-body text-xs text-[#8A8A82] mb-5 max-w-2xl">
        Send a secure onboarding link. Anyone accepting the invite gets auto-linked as your dependent (their orders route to you for approval, commissions settle to {user.buyer_category === "custodian" ? "your capital sub-account" : "your sponsor account"}). Links expire in 7 days.
      </p>

      <form onSubmit={submit} className="border border-white/10 rounded-sm bg-[#0a0a0a] p-5 grid md:grid-cols-[1fr_1fr_2fr_auto] gap-3 items-end" data-testid="sponsor-invite-form">
        <div>
          <label className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-1">Applicant email *</label>
          <input data-testid="sponsor-invite-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                 className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333]" />
        </div>
        <div>
          <label className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-1">Business name (optional)</label>
          <input data-testid="sponsor-invite-biz" value={biz} onChange={(e) => setBiz(e.target.value)}
                 className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333]" />
        </div>
        <div>
          <label className="font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] block mb-1">Personal note (optional)</label>
          <input data-testid="sponsor-invite-note" value={note} onChange={(e) => setNote(e.target.value)}
                 className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 font-body text-sm text-[#F5F5F0] focus:outline-none focus:border-[#B87333]" />
        </div>
        <button data-testid="sponsor-invite-submit" type="submit" disabled={sending}
                className="bg-[#B87333] hover:bg-[#A05F26] text-black font-grotesk text-[10px] uppercase tracking-[0.24em] px-5 py-2.5 rounded-sm disabled:opacity-50 flex items-center gap-2 shrink-0">
          <Send size={13} /> {sending ? "Sending…" : "Send invite"}
        </button>
      </form>

      {invites.length > 0 && (
        <div className="mt-4 border border-white/10 rounded-sm bg-[#0a0a0a] overflow-hidden">
          <div className="px-5 py-3 border-b border-white/[0.06] flex items-center justify-between">
            <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82]">Sent invites</span>
            <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#B87333]">{invites.length}</span>
          </div>
          <ul>
            {invites.map((inv) => {
              const used = !!inv.used_at;
              const expired = !used && inv.expires_at && new Date(inv.expires_at) < new Date();
              const status = used ? "used" : expired ? "expired" : "pending";
              const StatusIcon = used ? CheckCircle2 : Clock;
              const statusColor = used ? "#2FB999" : expired ? "#8A8A82" : "#B87333";
              return (
                <li key={inv.id} data-testid={`invite-row-${inv.id}`} className="px-5 py-3 grid md:grid-cols-[1fr_auto_auto_auto] items-center gap-3 border-b border-white/[0.04] last:border-b-0">
                  <div className="min-w-0">
                    <div className="font-body text-sm text-[#F5F5F0] truncate">{inv.email}</div>
                    <div className="font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#8A8A82] mt-0.5">
                      {inv.business_name || "—"} · sent {new Date(inv.created_at).toLocaleDateString("en-IN")}
                    </div>
                  </div>
                  <span className="font-grotesk text-[9px] uppercase tracking-[0.2em] flex items-center gap-1.5 px-2 py-1 rounded-sm border" style={{ color: statusColor, borderColor: statusColor + "60", background: statusColor + "10" }}>
                    <StatusIcon size={11} /> {status}
                    {used && inv.used_by_email ? <span className="text-[#C9C9C2]"> · {inv.used_by_email}</span> : null}
                  </span>
                  {!used && !expired && (
                    <button data-testid={`invite-copy-${inv.id}`} type="button" onClick={() => copy(inv.invite_url)}
                            className="border border-white/15 hover:border-[#B87333] text-[#C9C9C2] hover:text-[#B87333] font-grotesk text-[9px] uppercase tracking-[0.22em] px-3 py-1.5 rounded-sm flex items-center gap-1.5">
                      <Copy size={11} /> Copy link
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};
